import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { argbToCssHex, cssHexToArgb } from '@/lib/color-format'
import { TagDto, TagTranslationDto, TagUpsertDto } from '@/common/models/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { ColorField } from '../settings/settings-components/ColorField'
import { TranslationTabs } from './shared/TranslationTabs'
import { translationText } from './shared/labels'

interface CreateTagModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tag?: TagDto | null
  onSubmit: (values: TagUpsertDto, id?: string) => void | Promise<void>
  onDelete?: (id: string) => void
}

interface ITagFormValues {
  /**
   * Form/UI state stays in CSS hex (`#RRGGBB[AA]`, alpha last) — the format `ColorField`
   * renders. Converted to/from the API's `#AARRGGBB` (alpha first) only at load and submit
   * via `argbToCssHex`/`cssHexToArgb`.
   */
  textColor: string
  backgroundColor: string
  translations: TagTranslationDto[]
}

const DEFAULT_TEXT_COLOR = '#FFFFFF'
const DEFAULT_BACKGROUND_COLOR = '#111111'

const COLOR_HEX_REGEX = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/

const colorSchema = yup
  .string()
  .required('Color is required')
  .matches(COLOR_HEX_REGEX, 'Invalid color')

const schema = yup.object().shape({
  textColor: colorSchema,
  backgroundColor: colorSchema,
  translations: yup.array().default([]),
})

const defaultValues: ITagFormValues = {
  textColor: DEFAULT_TEXT_COLOR,
  backgroundColor: DEFAULT_BACKGROUND_COLOR,
  translations: [],
}

export const CreateTagModal = ({
  open,
  onOpenChange,
  tag,
  onSubmit,
  onDelete,
}: CreateTagModalProps) => {
  const isEditMode = !!tag
  const [deleteOpen, setDeleteOpen] = useState(false)

  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<ITagFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues,
  })

  useEffect(() => {
    if (!open) return
    if (tag) {
      setValue('textColor', argbToCssHex(tag.textColor) ?? DEFAULT_TEXT_COLOR, {
        shouldValidate: true,
      })
      setValue(
        'backgroundColor',
        argbToCssHex(tag.backgroundColor) ?? DEFAULT_BACKGROUND_COLOR,
        { shouldValidate: true },
      )
      setValue('translations', tag.translations ?? [], { shouldValidate: true })
    }
    return () => {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tag])

  const handleClose = () => onOpenChange(false)

  const submit = async (values: ITagFormValues) => {
    // Await the caller: only close once the create/update actually settles, so a failed request
    // leaves the modal open (with the user's input intact) instead of vanishing as if it succeeded.
    try {
      await onSubmit(
        {
          textColor: cssHexToArgb(values.textColor) ?? '',
          backgroundColor: cssHexToArgb(values.backgroundColor) ?? '',
          translations: values.translations,
        },
        tag?.id,
      )
      handleClose()
    } catch {
      // Keep the modal open; the caller surfaces the error to the user.
    }
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="tag"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Tag</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode && onDelete ? (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isEditMode && Object.keys(dirtyFields).length === 0}
                form="create-tag-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-tag-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ColorField
              control={control}
              name="textColor"
              label="Text Colour"
              format="hex"
              error={errors?.textColor?.message}
            />
            <ColorField
              control={control}
              name="backgroundColor"
              label="Background Colour"
              format="hex"
              error={errors?.backgroundColor?.message}
            />
          </div>

          <Controller
            name="translations"
            control={control}
            render={({ field }) => (
              <TranslationTabs<TagTranslationDto>
                value={field.value}
                onChange={field.onChange}
                makeEmpty={(locale) => ({ locale, text: '' })}
                renderFields={({ value, onFieldChange }) => (
                  <FormGroup label="Text">
                    <Input
                      value={value.text ?? ''}
                      onChange={(e) => onFieldChange({ text: e.target.value })}
                      type="text"
                      placeholder="Enter tag text"
                    />
                  </FormGroup>
                )}
              />
            )}
          />
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deleteTitle="Tag"
        deletedItem={
          tag
            ? `${translationText(
                tag.translations,
                'this tag',
              )} — this also detaches it from every quest and quest group`
            : undefined
        }
        deleteFunc={() => {
          if (tag && onDelete) onDelete(tag.id)
          handleClose()
        }}
      />
    </>
  )
}
